import axios from "axios";
import { getValidToken } from "../../utils/authUtils";

export const financeApi = {
  getTransactionsByPartyType: async (
    partyType: string,
    financeType: string
  ) => {
    try {
      const token = await getValidToken();
      let url = "";
      if (financeType === "income") {
        url = `${process.env.REACT_APP_API_HOST}finance/income/recent-transactions/${partyType}`;
      } else {
        url = `${process.env.REACT_APP_API_HOST}finance/expense/recent-transactions/${partyType}`;
      }
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response?.data?.isSuccess) {
        return response?.data?.data;
      } else {
        throw new Error(response?.data?.message);
      }
    } catch (error) {
      throw error;
    }
  },
  getPartyListWithBalance: async (partyType: string, financeType: string) => {
    try {
      const token = await getValidToken();
      let url = "";
      if (financeType === "income") {
        url = `${process.env.REACT_APP_API_HOST}finance/income/party-list/${partyType}/contract`;
      } else {
        url = `${process.env.REACT_APP_API_HOST}finance/expense/party-list/${partyType}`;
      }
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response?.data?.isSuccess) {
        return response?.data?.data;
      } else {
        throw new Error(response?.data?.message);
      }
    } catch (error) {
      throw error;
    }
  },

  getTransactionsByPartyId: async (
    partyType: string,
    partyId: string,
    financeType: string,
    page: number = 1,
    limit: number = 10,
    startDate?: string | null,
    endDate?: string | null
  ) => {
    try {
      const token = await getValidToken();
      let url = "";

      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("limit", limit.toString());

      // Add date filters if provided
      if (startDate) {
        queryParams.append("startDate", startDate);
      }
      if (endDate) {
        queryParams.append("endDate", endDate);
      }

      if (financeType === "income") {
        url = `${
          process.env.REACT_APP_API_HOST
        }finance/income/party-transactions/${partyType}/${partyId}?${queryParams.toString()}`;
      } else {
        url = `${
          process.env.REACT_APP_API_HOST
        }finance/expense/party-transactions/${partyType}/${partyId}?${queryParams.toString()}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response?.data?.isSuccess) {
        return response?.data?.data;
      } else {
        throw new Error(response?.data?.message);
      }
    } catch (error) {
      throw error;
    }
  },

  createTransaction: async (transactionData: any) => {
    try {
      const token = await getValidToken();
      const url = `${process.env.REACT_APP_API_HOST}finance/party-transactions/${transactionData?.partyType}`;
      const response = await axios.post(url, transactionData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response?.data?.isSuccess) {
        return response?.data?.data;
      } else {
        throw new Error(response?.data?.message);
      }
    } catch (error) {
      throw error;
    }
  },

  getExpenseSummary: async () => {
    try {
      const token = await getValidToken();
      const url = `${process.env.REACT_APP_API_HOST}finance/expense/summary`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response?.data?.isSuccess) {
        return response?.data?.data;
      } else {
        throw new Error(response?.data?.message);
      }
    } catch (error) {
      throw error;
    }
  },

  getIncomeSummary: async () => {
    try {
      const token = await getValidToken();
      const url = `${process.env.REACT_APP_API_HOST}finance/income/summary`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response?.data?.isSuccess) {
        return response?.data?.data;
      } else {
        throw new Error(response?.data?.message);
      }
    } catch (error) {
      throw error;
    }
  },

  getCashBookData: async (date: string) => {
    try {
      const token = await getValidToken();
      const queryParams = new URLSearchParams();
      queryParams.append("date", date);

      const url = `${
        process.env.REACT_APP_API_HOST
      }finance/cashbook?${queryParams.toString()}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response?.data?.isSuccess) {
        return response?.data?.data;
      } else {
        throw new Error(response?.data?.message);
      }
    } catch (error) {
      throw error;
    }
  },
  createCashBook: async (
    date: string,
    totalCredit: number,
    totalDebit: number,
    totalBalance: number,
    totalCashBalance: number,
    totalOnlineBalance: number
  ) => {
    try {
      const token = await getValidToken();
      const url = `${process.env.REACT_APP_API_HOST}finance/cashbook`;
      const response = await axios.post(
        url,
        {
          date,
          totalCredit,
          totalDebit,
          totalBalance,
          totalCashBalance,
          totalOnlineBalance,
          isLocked: true,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response?.data?.isSuccess) {
        return response?.data?.data;
      } else {
        throw new Error(response?.data?.message);
      }
    } catch (error) {
      throw error;
    }
  },
  updateCashBook: async (cashBookId: string, cashBookData: any) => {
    try {
      const token = await getValidToken();
      const url = `${process.env.REACT_APP_API_HOST}finance/cashbook/${cashBookId}`;
      const response = await axios.put(url, cashBookData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response?.data?.isSuccess) {
        return response?.data?.data;
      } else {
        throw new Error(response?.data?.message);
      }
    } catch (error) {
      throw error;
    }
  },
  patchCashBook: async (cashBookId: string, isLocked: boolean) => {
    try {
      const token = await getValidToken();
      const url = `${process.env.REACT_APP_API_HOST}finance/cashbook/${cashBookId}`;
      const response = await axios.patch(
        url,
        { isLocked },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response?.data?.isSuccess) {
        return response?.data?.data;
      } else {
        throw new Error(response?.data?.message);
      }
    } catch (error) {
      throw error;
    }
  },
};
